//
// Copyright (c) 2021 Astute Global, Inc.
//

import { Plugin, toWidget, viewToModelPositionOutsideModelElement, Widget } from "ckeditor5";
import { ConversationVariableCommand } from "./conversationVariableCommand";
import { PLACEHOLDER_VALUE } from "../ckEditorConstants";

export default class ConversationVariableEditing extends Plugin {
  // noinspection JSUnusedGlobalSymbols
  static get requires() {
    return [Widget];
  }

  // noinspection JSUnusedGlobalSymbols
  static get pluginName() {
    return "ConversationVariableEditing";
  }

  init() {
    const editor = this.editor;
    this._setupConfig();
    this._setupSchema();
    this._setupConversions();
    editor.commands.add("conversationVariable", new ConversationVariableCommand(editor));
    editor.editing.mapper.on(
      "viewToModelPosition",
      viewToModelPositionOutsideModelElement(editor.model, (viewElement) =>
        viewElement.hasClass("conversationVariable"),
      ),
    );
  }

  _createViewElement(modelItem, writer, allowPlaceholder) {
    const insertType = modelItem.getAttribute("insertType");
    const variableId = modelItem.getAttribute("variableId");
    if (variableId == PLACEHOLDER_VALUE && !allowPlaceholder) {
      return null;
    }
    const variableName = modelItem.getAttribute("variableName");
    const element = writer.createContainerElement("span", {
      class: "fieldinsert",
      "data-akinserttype": insertType,
      "data-akvariable": variableId,
    });
    const text = writer.createText(variableName);
    writer.insert(writer.createPositionAt(element, 0), text);
    return element;
  }

  _setupConfig() {
    this.editor.config.define("conversationVariable", {
      emptyVariableName: "Please select a Conversation Variable.",
    });
  }

  _setupConversions() {
    const conversion = this.editor.conversion;
    conversion.for("upcast").elementToElement({
      view: {
        name: "span",
        attributes: ["data-akvariable", "data-akinserttype"],
        classes: ["fieldinsert"],
      },
      model: (viewElement, { writer }) => {
        const insertType = viewElement.getAttribute("data-akinserttype");
        const variableId = viewElement.getAttribute("data-akvariable");
        const variableName = viewElement.getChild(0).data;
        return writer.createElement("conversationVariable", {
          insertType,
          variableId,
          variableName,
        });
      },
    });
    conversion.for("dataDowncast").elementToElement({
      model: "conversationVariable",
      view: (modelItem, { writer }) => this._createViewElement(modelItem, writer),
    });
    conversion.for("editingDowncast").elementToElement({
      model: "conversationVariable",
      view: (modelItem, { writer }) => {
        const element = this._createViewElement(modelItem, writer, true);
        if (!element) {
          return null;
        }
        return toWidget(element, writer);
      },
    });
  }

  _setupSchema() {
    this.editor.model.schema.register("conversationVariable", {
      allowWhere: "$text",
      inheritTypesFrom: "$text",
      isObject: true,
      allowAttributes: ["insertType", "variableId", "variableName"],
    });
  }
}
